(function (win) {
  'use strict';
  if (win.SickstyleMeasurement) return;
  const instances = new WeakMap();
  const locales = new Set(['de', 'en', 'fr', 'it', 'es', 'nl', 'pl', 'cs', 'sv', 'da']);
  const categories = new Set(['mx', 'street', 'emoto']);
  const positions = new Set(['hero', 'grid', 'product', 'sticky', 'footer']);
  const id = value => typeof value === 'string' && /^[1-9][0-9]{0,19}$/.test(value) ? value : null;
  const responseId = value => typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? String(value) : id(value);
  const token = value => value && typeof value === 'object';
  const allowed = () => {
    try { return win.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch (_) { return false; }
  };
  function create(root, config) {
    if (!token(root) || !config || !id(config.themeId) || !categories.has(config.category) || !locales.has(config.locale)) return null;
    if (instances.has(root)) return instances.get(root);
    const products = new Map();
    for (const item of Array.isArray(config.products) ? config.products : []) {
      if (!id(item?.productId) || !Array.isArray(item.variantIds)) continue;
      const variants = new Set(item.variantIds.filter(value => id(value)));
      if (variants.size) products.set(item.productId, new Set([...(products.get(item.productId) || []), ...variants]));
    }
    const base = {schema_version: 1, flow_version: 'mx-native-v1', category: config.category, locale: config.locale, theme_id: config.themeId};
    if (id(config.pageId)) base.page_id = config.pageId;
    if (id(config.modelPageId)) base.model_page_id = config.modelPageId;
    const productId = products.has(config.productId) ? config.productId : null;
    const mode = config.mode === 'publish' ? 'publish' : config.mode === 'off' ? 'off' : 'qa';
    const role = config.themeRole;
    const designMode = config.designMode === true;
    const actions = new WeakMap();
    const pendingAdds = new WeakSet();
    const records = [];
    let productViewed = false;
    let landingViewed = false;
    const record = (event, status, payload) => {
      if (mode === 'off') return;
      records.push(payload ? {event, status, payload} : {event, status});
      if (records.length > 100) records.shift();
    };
    const publishing = () => !designMode && mode === 'publish' && role === 'main' && win.Shopify?.theme?.role === 'main' && String(win.Shopify?.theme?.id) === base.theme_id && win.Shopify?.designMode !== true && win.Shopify?.previewMode !== true && !/[?&]preview_theme_id=/.test(win.location?.search || '');
    function emit(name, fields) {
      const event = 'ss_mx:' + name;
      if (mode === 'off') return false;
      if (!allowed()) { record(event, 'blocked_consent'); return false; }
      const payload = Object.freeze({...base, ...fields, environment: publishing() ? 'production' : 'qa'});
      if (!publishing()) { record(event, 'qa_only', payload); return true; }
      if (typeof win.SickstyleGoogleDesign?.send !== 'function') { record(event, 'receiver_unavailable'); return false; }
      try {
        if (win.SickstyleGoogleDesign.send(event, payload) !== true) { record(event, 'receiver_not_ready_or_rejected'); return false; }
        record(event, 'google_event_called');
        return true;
      } catch (_) { record(event, 'receiver_failed'); return false; }
    }
    function actionOnce(action, name, fields) {
      if (!token(action)) return false;
      const seen = actions.get(action) || new Set();
      if (seen.has(name)) return false;
      seen.add(name);
      actions.set(action, seen);
      return emit(name, fields);
    }
    const productFields = (selectedProduct, selectedVariant) => {
      if (!products.get(selectedProduct)?.has(selectedVariant)) return null;
      return {product_id: selectedProduct, variant_id: selectedVariant};
    };
    const api = Object.freeze({
      landingView() {
        if (landingViewed || !base.page_id) return false;
        if (!emit('landing_view', {})) return false;
        landingViewed = true;
        return true;
      },
      productView() {
        if (productViewed || !productId) return false;
        if (!emit('product_view', {product_id: productId})) return false;
        productViewed = true;
        return true;
      },
      designSelect(action, selectedProduct, selectedVariant, position) {
        const fields = productFields(selectedProduct, selectedVariant);
        if (!fields || !positions.has(position)) return false;
        return actionOnce(action, 'design_select', {...fields, cta_position: position});
      },
      inquiryOpen(action, position) {
        if (!positions.has(position)) return false;
        return actionOnce(action, 'inquiry_open', {cta_position: position});
      },
      editorHandoffClick(action, selectedProduct, selectedVariant, position) {
        const fields = productFields(selectedProduct, selectedVariant);
        if (!fields || !positions.has(position)) return false;
        return actionOnce(action, 'editor_handoff_click', {...fields, cta_position: position});
      },
      beginNativeAdd() {
        const action = Object.freeze({});
        pendingAdds.add(action);
        return action;
      },
      nativeAddConfirmed(action, response, httpStatus) {
        if (!token(action) || !pendingAdds.has(action)) return false;
        pendingAdds.delete(action);
        if (!Number.isInteger(httpStatus) || httpStatus < 200 || httpStatus >= 300 || !response || typeof response !== 'object' || response.status || response.errors || response.description) return false;
        const items = Array.isArray(response.items) ? response.items : [response];
        if (!items.length || items.length > 100) return false;
        const safeItems = [];
        for (const item of items) {
          const product = responseId(item?.product_id);
          const variant = responseId(item?.variant_id ?? item?.id);
          const quantity = item?.quantity;
          if (!products.get(product)?.has(variant) || !Number.isInteger(quantity) || quantity < 1 || quantity > 10000) return false;
          safeItems.push(Object.freeze({product_id: product, variant_id: variant, line_quantity_after: quantity}));
        }
        return actionOnce(action, 'native_add_confirmed', {items: Object.freeze(safeItems)});
      },
      inspect() { return JSON.parse(JSON.stringify(records)); },
      clearDiagnostics() { records.length = 0; }
    });
    instances.set(root, api);
    return api;
  }
  win.SickstyleMeasurement = Object.freeze({version: 1, create});

  const mountedScopes = new Set();
  function mount(node) {
    if (node.ssMeasurementMounted) return;
    const root = node.closest('[data-ss-measure-scope]');
    if (!root) return;
    let config;
    try { config = JSON.parse(node.textContent); } catch (_) { return; }
    if (config.kind !== 'landing' && config.kind !== 'product') return;
    if (config.kind === 'product') {
      const sources = new URLSearchParams(win.location.search).getAll('ss_model_page');
      const source = sources.length === 1 ? sources[0] : null;
      if (id(source) && Array.isArray(config.allowedModelPageIds) && config.allowedModelPageIds.includes(source)) config.modelPageId = source;
    }
    const tracker = create(root, config);
    if (!tracker) return;
    node.ssMeasurementMounted = true;
    root.ssMeasurement = tracker;
    if (config.kind === 'product' && config.nativeDecor === true) {
      for (const form of root.querySelectorAll('form[data-ss-native-decor="true"]')) form.ssMeasurement = tracker;
    }
    for (const scope of mountedScopes) if (!scope.root.isConnected) mountedScopes.delete(scope);
    mountedScopes.add({root, tracker, kind: config.kind});
    const view = () => {
      if (!root.isConnected) return;
      if (config.kind === 'product') tracker.productView();
      else tracker.landingView();
    };
    if (win.Shopify?.customerPrivacy || typeof win.Shopify?.loadFeatures !== 'function') view();
    else {
      try { win.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.2'}], () => view()); }
      catch (_) { view(); }
    }
  }
  function mountWithin(container) {
    for (const node of container.querySelectorAll('[data-ss-measure-config]')) mount(node);
  }
  if (win.document) {
    const currentViews = () => {
      for (const scope of mountedScopes) {
        if (!scope.root.isConnected) { mountedScopes.delete(scope); continue; }
        if (scope.kind === 'product') scope.tracker.productView();
        else scope.tracker.landingView();
      }
    };
    win.document.addEventListener('ss-mx-receiver-ready', currentViews);
    win.document.addEventListener('visitorConsentCollected', () => {
      if (!allowed()) return;
      currentViews();
    });
    win.document.addEventListener('click', event => {
      if (!event.isTrusted || event.button > 0) return;
      const link = event.target.closest?.('a');
      if (!link) return;
      let scopes = [...mountedScopes].filter(scope => scope.root.isConnected && scope.root.contains(link));
      if (scopes.length === 0 && link.closest('.mx-questions') && link.getAttribute('href') === '#ss-anfrage') {
        scopes = [...mountedScopes].filter(scope => scope.kind === 'landing' && scope.root.isConnected);
      }
      if (scopes.length !== 1) return;
      const {tracker} = scopes[0];
      if (link.dataset.ssMeasureAction === 'design') {
        tracker.designSelect(event, link.dataset.ssMeasureProduct, link.dataset.ssMeasureVariant, 'grid');
      } else if (link.getAttribute('href') === '#ss-anfrage') {
        const position = link.closest('.mx-model__hero') ? 'hero' : 'footer';
        tracker.inquiryOpen(event, position);
      }
    });
    if (win.document.readyState === 'loading') win.document.addEventListener('DOMContentLoaded', () => mountWithin(win.document), {once: true});
    else mountWithin(win.document);
    win.document.addEventListener('shopify:section:load', event => mountWithin(event.target));
  }
})(window);

